import type { ComponentType } from 'react';
import type { AnyFieldApi } from '@tanstack/react-form';
import type { BoundSelectorProps } from '@/shared/ui';

/**
 * Props pole nad typovým selektorem. Consumer řeší jen `mode`, `label` a popisné texty;
 * `selected` / `onChange` / `error` se mostí z TanStack Form pole.
 */
export type AsyncSelectorFieldProps = Omit<
  BoundSelectorProps<unknown>,
  'selected' | 'onChange' | 'error'
> & {
  /** TanStack Form field API. V `multi` je hodnota `TItem[]`, v `single` `TItem | null`. */
  field: AnyFieldApi;
};

/**
 * Generický wrapper, který z typového selektoru (`FC<BoundSelectorProps<TItem>>`, např.
 * `DestinationSelector`) vyrobí komponentu napojenou na TanStack Form. Sjednocuje původní
 * `AsyncSingleSelectField` + `AsyncMultiSelectField` – chování se větví podle `mode`:
 *
 * - `multi`  — hodnota pole je `TItem[]`; `null ↔ []` se neřeší, prázdné pole je `[]`.
 * - `single` — hodnota pole je `TItem | null`; na hranici se mapuje `null ↔ []`.
 *
 * Použití v typovém selektoru:
 * `export const DestinationSelectorField = createAsyncSelectorField(DestinationSelector);`
 */
export function createAsyncSelectorField<TItem>(
  Selector: ComponentType<BoundSelectorProps<TItem>>
) {
  function AsyncSelectorField({ field, mode, ...rest }: AsyncSelectorFieldProps) {
    const error = field.state.meta.isTouched
      ? (field.state.meta.errors[0]?.message as string | undefined)
      : undefined;

    if (mode === 'multi') {
      return (
        <Selector
          {...rest}
          mode="multi"
          selected={(field.state.value as readonly TItem[] | undefined) ?? []}
          onChange={(next) => field.handleChange([...next])}
          error={error}
        />
      );
    }

    const value = field.state.value as TItem | null | undefined;
    return (
      <Selector
        {...rest}
        mode="single"
        selected={value ? [value] : []}
        onChange={(next) => field.handleChange(next[0] ?? null)}
        error={error}
      />
    );
  }

  return AsyncSelectorField;
}
